/* Source detail — the stored source doc in the ONE loop, plus the origin rule
 * made visible: per-capability origin chips (ingested · modified · manual).
 * (Re-ingest was removed; a source is refreshed by connecting it again.)
 *
 * A-0017: a source wired to an INTERNAL, platform-provisioned connection
 * shows that as a read-only chip above the editor — never as an editable
 * connection blob a user could "fix" by pasting a raw URI. */
import React, {
    useMemo,
    useState
} from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from 'shared/components/ui/dialog';
import {
    ListTree,
    ShieldCheck
} from 'lucide-react';
import DefEditorScreen from '../../shared/DefEditorScreen';
import SourceCapabilityMap from '../../shared/visuals/SourceCapabilityMap';
import {
    OperationTypeBadge,
    OriginChip
} from '../../shared/badges';
import type {
    Capability
} from '../../api/types';

function parseCapabilities(text: string): Capability[] {
    try {
        const doc = JSON.parse(text);
        return Array.isArray(doc?.capabilities) ? doc.capabilities : [];
    } catch {
        return [];
    }
}

/** A-0017 — the connection entries a source resolves through the PLATFORM
 * (`connections[].internal` names an internal-connection id). Their endpoint
 * and keys live server-side and are never part of this document. */
export function parseInternalConnections(text: string): any[] {
    try {
        const doc = JSON.parse(text);
        const conns = Array.isArray(doc?.connections) ? doc.connections : [];
        return conns.filter((c: any) => c && typeof c === 'object'
            && typeof c.internal === 'string' && c.internal);
    } catch {
        return [];
    }
}

/** Read-only chip per internal connection. The point is that this is a CHIP
 * and not an editable JSON blob: there is no endpoint or key here to "fix",
 * and a raw URI pasted in its place would only break the source — the engine
 * resolves the real connection from the internal id. */
export function InternalConnectionStrip({
    text
}: {
    text: string;
}) {
    const conns = useMemo(() => parseInternalConnections(text), [text]);
    if (conns.length === 0) return null;
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '12px 20px 0'
        }}>
            {conns.map((c: any, i: number) => {
                const bucket = c.config && typeof c.config === 'object'
                    ? c.config.bucket : undefined;
                return (
                    <span key={c.id || c.internal || i}
                        className="aps-badge aps-badge-success"
                        title={'Platform-managed connection — resolved server-side'
                            + ' from ' + String(c.internal) + '. Its credentials are'
                            + ' not in this document and never reach the browser.'}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5
                        }}>
                        <ShieldCheck size={11} />
                        {c.name || String(c.internal)}
                        {bucket ? (
                            <span className="aps-mono" style={{
                                opacity: 0.8
                            }}>
                                · {String(bucket)}
                            </span>
                        ) : null}
                    </span>
                );
            })}
            <span className="aps-help" style={{
                marginTop: 0
            }}>
                Internal connection — read-only. The endpoint and keys stay on
                the server; this document holds only the bucket.
            </span>
        </div>
    );
}

function OriginStrip({
    text,
    onOpenList
}: {
    text: string;
    onOpenList: () => void;
}) {
    const caps = useMemo(() => parseCapabilities(text), [text]);
    const mix = useMemo(() => {
        const m: Record<string, number> = {};
        for (const c of caps) {
            const o = c.origin || 'ingested';
            m[o] = (m[o] || 0) + 1;
        }
        return m;
    }, [caps]);
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '12px 20px 0'
        }}>
            <button className="aps-btn aps-btn-secondary aps-btn-sm" onClick={onOpenList}>
                <ListTree size={14} />
                Capabilities ({caps.length})
            </button>
            {Object.entries(mix).map(([origin, n]) => (
                <span key={origin} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5
                }}>
                    <OriginChip origin={origin} />
                    <span className="aps-mono" style={{
                        fontSize: 'var(--aps-text-xs)',
                        color: 'var(--aps-text-muted)'
                    }}>
                        ×{n}
                    </span>
                </span>
            ))}
            <span className="aps-help" style={{
                marginTop: 0
            }}>
                Origin shows how each capability got here: ingested (from connect),
                modified (you edited it), manual (you added it).
            </span>
        </div>
    );
}

export default function SourceDetail({
    id,
    onChanged
}: {
    id: string;
    onChanged: () => void;
}) {
    const [capsOpen, setCapsOpen] = useState(false);
    const [capsText, setCapsText] = useState('');

    const caps = useMemo(() => parseCapabilities(capsText), [capsText]);

    return (
        <>
            <DefEditorScreen
                key={id}
                type="source"
                id={id}
                onDeleted={onChanged}
                onSaved={onChanged}
                visual={(text) => <SourceCapabilityMap docText={text} />}
                beforeEditor={(text) => (
                    <>
                        <InternalConnectionStrip text={text} />
                        <OriginStrip text={text} onOpenList={() => {
                            setCapsText(text);
                            setCapsOpen(true);
                        }} />
                    </>
                )}
            />
            <Dialog open={capsOpen} onOpenChange={setCapsOpen}>
                <DialogContent className="sm:max-w-[640px]">
                    <DialogHeader>
                        <DialogTitle>Capabilities — {id}</DialogTitle>
                    </DialogHeader>
                    <div style={{
                        maxHeight: '60vh',
                        overflowY: 'auto'
                    }}>
                        <div className="aps-table-wrap">
                            <table className="aps-table">
                                <thead>
                                    <tr>
                                        <th>Capability</th>
                                        <th>Type</th>
                                        <th>Risk</th>
                                        <th>Origin</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {caps.map((c) => (
                                        <tr key={c.id} style={{
                                            cursor: 'default'
                                        }}>
                                            <td>
                                                <div className="aps-mono" style={{
                                                    fontWeight: 600
                                                }}>
                                                    {c.id}
                                                </div>
                                                <div style={{
                                                    fontSize: 'var(--aps-text-xs)',
                                                    color: 'var(--aps-text-muted)'
                                                }}>
                                                    {c.description || c.name || ''}
                                                </div>
                                            </td>
                                            <td><OperationTypeBadge type={c.operation_type} /></td>
                                            <td className="aps-mono">{c.risk_level || '—'}</td>
                                            <td><OriginChip origin={c.origin} /></td>
                                        </tr>
                                    ))}
                                    {caps.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="aps-muted">
                                                No capabilities in the current document.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
